import { useState } from "react";
import { durationOptions, sortOptions, type DurationKey, type SortKey } from "@/lib/videos";

interface VideoFiltersProps {
  sort: SortKey;
  duration: DurationKey;
  onSortChange: (value: SortKey) => void;
  onDurationChange: (value: DurationKey) => void;
  onReset: () => void;
  resultCount: number;
  showDuration?: boolean;
}

function OptionButton({
  active,
  label,
  onClick,
  className = "",
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
        active
          ? "bg-primary-500 font-medium text-background-50"
          : "bg-background-100 text-foreground-600 hover:bg-background-200"
      } ${className}`}
    >
      {label}
    </button>
  );
}

export default function VideoFilters({
  sort,
  duration,
  onSortChange,
  onDurationChange,
  onReset,
  resultCount,
  showDuration = true,
}: VideoFiltersProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <>
      <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-3 md:px-5">
        <div className="hidden flex-wrap items-center justify-between gap-4 md:flex">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="font-label text-xs text-foreground-500">排序</span>
              <div className="flex items-center gap-1.5">
                {sortOptions.map((option) => (
                  <OptionButton
                    key={option.value}
                    active={sort === option.value}
                    label={option.label}
                    onClick={() => onSortChange(option.value)}
                  />
                ))}
              </div>
            </div>
            {showDuration && (
              <div className="flex items-center gap-2">
                <span className="font-label text-xs text-foreground-500">时长</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {durationOptions.map((option) => (
                    <OptionButton
                      key={option.value}
                      active={duration === option.value}
                      label={option.label}
                      onClick={() => onDurationChange(option.value)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="font-label text-xs text-foreground-500">
              共 <span className="font-semibold text-foreground-900">{resultCount}</span> 条
            </span>
            <button
              type="button"
              onClick={onReset}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs text-foreground-500 transition-colors hover:bg-background-100 hover:text-primary-600"
            >
              <i className="ri-refresh-line text-sm"></i>
              重置
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 md:hidden">
          <span className="font-label text-xs text-foreground-500">
            共 <span className="font-semibold text-foreground-900">{resultCount}</span> 条视频
          </span>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-800"
          >
            <i className="ri-equalizer-line text-base text-primary-500"></i>
            筛选与排序
          </button>
        </div>
      </div>

      {sheetOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 cursor-pointer bg-foreground-950/45"
          ></button>
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-background-50 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选与排序</h3>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-600"
                aria-label="关闭"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <p className="mt-5 font-label text-xs text-foreground-500">排序方式</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSortChange(option.value)}
                  className={`cursor-pointer rounded-md px-3 py-3 text-sm transition-colors ${
                    sort === option.value
                      ? "bg-primary-500 font-medium text-background-50"
                      : "bg-background-100 text-foreground-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {showDuration && (
              <>
                <p className="mt-5 font-label text-xs text-foreground-500">视频时长</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {durationOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onDurationChange(option.value)}
                      className={`cursor-pointer rounded-md px-3 py-3 text-sm transition-colors ${
                        duration === option.value
                          ? "bg-primary-500 font-medium text-background-50"
                          : "bg-background-100 text-foreground-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={onReset}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 py-3 text-sm text-foreground-700"
              >
                重置条件
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50"
              >
                查看 {resultCount} 条结果
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}